import React, { useEffect, useState } from 'react';
import WindowedSelect from 'react-windowed-select';
import CreatableSelect from 'react-select/creatable';
import api from '../../services/saleVoucherRAApi';
import { COST_CENTRE_LEVELS } from './saleVoucherConstants';
import { Field, selectProps, toSelectOptions } from './formParts';

export const costCentreLabel = (settings, n) => settings?.labels?.[`costCenter${n}`] ?? `Cost Center ${n}`;

/**
 * Options for the four cascading levels. Level n+1 is fetched once the levels above it are set
 * (the api layer caches by query, so many rows asking for the same list cost one request).
 */
const useLevelOptions = ({ cc1, cc2, cc3 }) => {
    const [options, setOptions] = useState({ 1: [], 2: [], 3: [], 4: [] });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [l1, l2, l3, l4] = await Promise.all([
                api.getCostCentreList({ level: 1 }),
                cc1 ? api.getCostCentreList({ level: 2, costCenter1: cc1 }) : [],
                cc1 && cc2 ? api.getCostCentreList({ level: 3, costCenter1: cc1, costCenter2: cc2 }) : [],
                cc1 && cc2 && cc3 ? api.getCostCentreList({ level: 4, costCenter1: cc1, costCenter2: cc2, costCenter3: cc3 }) : [],
            ]);
            if (!cancelled) setOptions({ 1: l1, 2: l2, 3: l3, 4: l4 });
        })();
        return () => { cancelled = true; };
    }, [cc1, cc2, cc3]);
    return options;
};

// Levels 1 and 2 are closed lists (property, unit). Levels 3 and 4 only list values already used, so they accept new text.
const OPEN_LEVELS = [3, 4];
export const COST_CENTRE_MAX_LENGTH = 255;
const isValidNewValue = (input) => {
    const v = input.trim();
    return v.length > 0 && v.length <= COST_CENTRE_MAX_LENGTH;
};

const pick = (opts, value) => (value ? { value, label: opts.find((o) => o.value === value)?.label || value } : null);

/**
 * values: { cc1..cc4 }. onChange(level, value) lets the owner clear the child levels.
 * layout "header" renders four labelled fields; "cells" renders four <td>s for a grid row.
 */
const CostCentreSelects = ({ values, onChange, settings, disabled, layout = 'header', errors = {}, levels = COST_CENTRE_LEVELS }) => {
    const options = useLevelOptions(values);

    return (
        <>
            {levels.map((n) => {
                const opts = toSelectOptions(options[n]);
                const open = OPEN_LEVELS.includes(n);
                const Component = open ? CreatableSelect : WindowedSelect;
                const select = (
                    <Component
                        {...selectProps}
                        isClearable
                        isDisabled={disabled || (n > 1 && !values[`cc${n - 1}`])}
                        placeholder={layout === 'cells' ? '' : open ? 'Select or type...' : 'None'}
                        value={pick(opts, values[`cc${n}`])}
                        options={opts}
                        onChange={(o) => onChange(n, open ? (o?.value || '').trim() : (o?.value || ''))}
                        {...(open ? {
                            // The typed text is stored as-is (trimmed): no upper-casing or reshaping.
                            isValidNewOption: isValidNewValue,
                            formatCreateLabel: (input) => `Use "${input.trim()}"`,
                            getNewOptionData: (input) => ({ value: input.trim(), label: input.trim() }),
                            noOptionsMessage: () => 'No values used yet. Type a new one.',
                        } : {})}
                        {...(layout === 'cells' ? { styles: { ...selectProps.styles, control: (base) => ({ ...base, minHeight: 32, minWidth: 140 }) } } : {})}
                    />
                );
                if (layout === 'cells') return <td key={n}>{select}</td>;
                return (
                    <Field
                        key={n}
                        label={costCentreLabel(settings, n)}
                        required={settings?.[`isCC${n}Required`] === true}
                        error={errors[`cc${n}`]}
                    >
                        {select}
                        {open && <div className="small text-muted mt-1">Pick an existing value or type a new one.</div>}
                    </Field>
                );
            })}
        </>
    );
};

export default CostCentreSelects;
